import React from "react";
import { Activity, UserRound } from "lucide-react";
import { DiscoveryPoint, RecruiterActivity } from "../Types/CandidateIntelligenceTypes";
import { Section, EmptyState, formatRelative } from "./Section";

/* Pure-SVG area chart (no chart library). */
export const DiscoveryAreaChart: React.FC<{ points: DiscoveryPoint[] }> = ({ points }) => {
  if (!points || points.length === 0) return <EmptyState label="No discovery data in this window" />;

  const w = 640;
  const h = 180;
  const padY = 12;
  const maxVal = Math.max(1, ...points.map((p) => Math.max(p.views, p.discoveries)));
  const stepX = points.length > 1 ? w / (points.length - 1) : 0;


  const coords = (val: number, index: number) => ({
    x: (points.length > 1 ? index * stepX : w / 2).toFixed(1),
    y: (h - padY - (val / maxVal) * (h - padY * 2)).toFixed(1),
  });
  

  const linePath = (key: "views" | "discoveries") =>
    points
      .map((p, i) => {
        const c = coords(p[key], i);
        return `${i === 0 ? "M" : "L"} ${c.x} ${c.y}`;
      })
      .join(" ");

  const areaPath = (key: "views" | "discoveries") => {
    const line = linePath(key);
    if (points.length === 0) return "";
    const last = coords(points[points.length - 1][key], points.length - 1);
    const first = coords(points[0][key], 0);
    return `${line} L ${last.x} ${h - padY} L ${first.x} ${h - padY} Z`;
  };

  const dots = (key: "views" | "discoveries") =>
    points.map((p, i) => {
      const c = coords(p[key], i);
      return <circle key={`${key}-${i}`} cx={c.x} cy={c.y} r={2} className="fill-slate-900 stroke-current" strokeWidth={1.5} />;
    });

  return (
    <div>
      <div className="mb-2 flex items-center gap-4 text-xs font-semibold">
        <span className="flex items-center gap-1.5 text-cyan-300">
          <span className="h-2 w-2 rounded-full bg-cyan-400" /> Views
        </span>
        <span className="flex items-center gap-1.5 text-indigo-300">
          <span className="h-2 w-2 rounded-full bg-indigo-400" /> Discoveries
        </span>
        <span className="ml-auto text-slate-500">{points.length} days</span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full rounded-xl border border-slate-800 bg-slate-950/50">
        <defs>
          <linearGradient id="viewsFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#22d3ee" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="discFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6366f1" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1="0" x2={w} y1={h - padY - f * (h - padY * 2)} y2={h - padY - f * (h - padY * 2)} className="stroke-slate-800" strokeDasharray="3 4" strokeWidth={1} />
        ))}
        <path d={areaPath("views")} fill="url(#viewsFill)" />
        <path d={linePath("views")} className="stroke-cyan-400" strokeWidth={2} fill="none" strokeLinejoin="round" strokeLinecap="round" />
        <path d={areaPath("discoveries")} fill="url(#discFill)" />
        <path d={linePath("discoveries")} className="stroke-indigo-400" strokeWidth={2} fill="none" strokeLinejoin="round" strokeLinecap="round" />
        <g className="text-cyan-400">{dots("views")}</g>
        <g className="text-indigo-400">{dots("discoveries")}</g>
      </svg>
    </div>
  );
};

const initials = (name: string | null) => {
  if (!name) return "?";
  return name
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
};

export const RecruiterActivityList: React.FC<{ recruiters: RecruiterActivity[] }> = ({ recruiters }) => {
  if (!recruiters.length) return <EmptyState label="No recruiter activity in this window" />;
  return (
    <ul className="flex flex-col gap-2">
      {recruiters.map((r, idx) => (
        <li key={r.recruiterUserId ?? `r-${idx}`} className="flex items-start gap-3 rounded-xl border border-slate-800 bg-slate-900/60 p-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-cyan-500/30 bg-cyan-500/10 text-xs font-black text-cyan-300">
            {initials(r.recruiterName)}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <p className="truncate text-sm font-bold text-slate-100">{r.recruiterName}</p>
              <span className="shrink-0 rounded-full bg-slate-800 px-2 py-0.5 text-[10px] font-bold text-slate-400">
                {r.activityCount} {r.activityCount === 1 ? "event" : "events"}
              </span>
            </div>
            <p className="truncate text-xs text-slate-400">
              {r.designation ? `${r.designation} · ` : ""}
              {r.companyName ?? ""}
            </p>
            {r.lastActivityMessage ? (
              <p className="mt-1 truncate text-xs text-slate-500">“{r.lastActivityMessage}”</p>
            ) : null}
            <p className="mt-0.5 text-[11px] text-slate-600">{formatRelative(r.lastActiveAt)}</p>
          </div>
        </li>
      ))}
    </ul>
  );
};

interface DiscoverySectionProps {
  discovery: DiscoveryPoint[];
  recruiters: RecruiterActivity[];
}

export const DiscoverySection: React.FC<DiscoverySectionProps> = ({ discovery, recruiters }) => (
  <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
    <Section
      icon={<Activity size={17} />}
      title="Profile Discovery Analytics"
      subtitle="Views vs. recruiter search discoveries over the selected window"
    >
      <DiscoveryAreaChart points={discovery} />
    </Section>
    <Section
      icon={<UserRound size={17} />}
      title="Recruiter Activity"
      subtitle="Who looked at your profile — safe, interaction-based insights"
    >
      <RecruiterActivityList recruiters={recruiters} />
    </Section>
  </div>
);